'use client'

// useAddToCart — the shared add-to-cart orchestrator (Overmind retirement,
// Bucket C). It reproduces the former Overmind `checkoutPageAddToCartClicked`
// action EXACTLY, but writes the cart line items to the Zustand cart store while
// leaving the non-cart side effects (region `served` guard, offers, the cart
// side-panel UI, the pre-order modal) on Overmind for now:
//
//   checkoutPageAddToCartClicked =
//     addItemToShoppingCart  → cartStore.add(ids, { served, shopItems })
//     sendAddToCartMetaPixelEvent → the pixel + dataLayer block below
//     updateShoppingCartInLocalStorage → (handled by the store's persist middleware)
//     { showShoppingCartSidePanel = true; reset preOrderWarning }
//                              → uiStore.openCart() + uiStore.closePreOrderWarning()
//
// `served` (can we ship to the visitor's country?) comes from the Zustand region
// store; `shopItems` (offers, for the multi-quantity clamp) comes from the
// RegionOffers seam (Phase D1) — both read here and passed INTO the pure cart store
// so the store stays decoupled. The cart side-panel + pre-order modal UI flags moved
// to the Zustand ui store (Phase C2 part 1).
import { useRegionOffers } from '../RegionOffers'
import { useServed } from '../../store/regionStore'
import { useCartStore } from '../../store/cartStore'
import { useUiStore } from '../../store/uiStore'
import { findItemById } from '../../utils/shopItems'
import { calculatePriceForMultipleShopItems } from '../../utils/shopItemsAndCart'
import sendMetaPixelEvent, { buildAddToCartEvent } from '../../utils/metaPixel'
import { pushEcommerceEvent, buildEcommercePayload } from '../../utils/dataLayer'

export function useAddToCart() {
  // MIXED READ: `served` comes from the Zustand region store (useServed); `offers`
  // (for the multi-quantity clamp) from the RegionOffers seam (Phase D1).
  const { offers } = useRegionOffers()
  const served = useServed()
  const add = useCartStore((s) => s.add)
  const openCart = useUiStore((s) => s.openCart)
  const closePreOrderWarning = useUiStore((s) => s.closePreOrderWarning)

  // Verbatim behaviour of the old checkoutPageAddToCartClicked action.
  const checkoutPageAddToCart = (ids) => {
    const { ids: addedIds } = add(ids, { served, shopItems: offers })

    // AddToCart pixel + GA4 — mirrors operators.sendAddToCartMetaPixelEvent, which
    // received the ids returned by the add operator (original ids when served, []
    // when the served=false buy-block fired).
    const items = addedIds.map((id) => findItemById(id, offers)).filter(Boolean)
    const price = calculatePriceForMultipleShopItems(items)
    sendMetaPixelEvent(buildAddToCartEvent(addedIds, price))
    pushEcommerceEvent('add_to_cart', buildEcommercePayload(items))

    // UI side effects (Zustand ui store): open the cart side-panel + clear any
    // pre-order warning. openCart sets showShoppingCartSidePanel=true;
    // closePreOrderWarning resets showPreOrderWarningModal + preOrderWarning —
    // together identical to the trailing mutation in checkoutPageAddToCartClicked.
    openCart()
    closePreOrderWarning()
  }

  return { checkoutPageAddToCart }
}
